import { Badge } from '@/components/ui/badge';
import {
  Severity,
  type SeverityType,
} from '@kirin/api-client/contracts/shared/severity';
import { getSeverityLabel } from '@kirin/api-client/helpers/severity';
import {
  AlertTriangle,
  AlertCircle,
  Shield,
  CheckCircle2,
  type LucideProps,
} from 'lucide-react';
import type { ReactElement } from 'react';

/**
 * Renders the appropriate icon for a severity level.
 * Icons follow a visual hierarchy: triangle (urgent) → circle (warning) → shield (caution) → check (safe)
 */
function SeverityIcon({
  severity,
  ...props
}: { severity: SeverityType | string } & LucideProps): ReactElement | null {
  switch (severity?.toLowerCase()) {
    case Severity.Critical:
      return <AlertTriangle {...props} />;
    case Severity.High:
      return <AlertCircle {...props} />;
    case Severity.Medium:
      return <Shield {...props} />;
    case Severity.Low:
      return <CheckCircle2 {...props} />;
    case 'info':
      return <CheckCircle2 {...props} />;
    default:
      return null;
  }
}

/*
  getSeverityBadgeVariant maps a Severity to the appropriate Badge variant.
  Returns the badge variant name for the given severity.
*/
function getSeverityBadgeVariant(
  severity: SeverityType | string
): 'critical' | 'high' | 'medium' | 'low' | 'secondary' {
  switch (severity?.toLowerCase()) {
    case Severity.Critical:
      return 'critical';
    case Severity.High:
      return 'high';
    case Severity.Medium:
      return 'medium';
    case Severity.Low:
      return 'low';
    case 'info':
      // Informational capabilities share the muted grey Low bubble treatment.
      return 'low';
    default:
      return 'secondary';
  }
}

interface SeverityBadgeProps {
  riskLevel: SeverityType | string;
  className?: string;
  /** Show icon before the label (default: false) */
  showIcon?: boolean;
  /** Custom label text (overrides default severity label) */
  label?: string;
  /**
   * Classes for the label, which then gets its own span (e.g.
   * `sr-only @lg:not-sr-only` for an icon-only badge in a narrow container)
   */
  labelClassName?: string;
}

/*
  SeverityBadge displays a badge with the appropriate styling and label for a given severity.
  Optionally displays an icon when showIcon is true.
*/
export function SeverityBadge({
  riskLevel,
  className,
  showIcon = false,
  label,
  labelClassName,
}: SeverityBadgeProps) {
  const text = label ?? getSeverityLabel(riskLevel);
  return (
    <Badge variant={getSeverityBadgeVariant(riskLevel)} className={className}>
      {showIcon && (
        <SeverityIcon severity={riskLevel} className="size-4 text-inherit" />
      )}
      {labelClassName ? <span className={labelClassName}>{text}</span> : text}
    </Badge>
  );
}
